import { Separator } from '@ibsys/kit';
import {
  BoldIcon,
  ItalicIcon,
  ListIcon,
  ListOrderedIcon,
  RemoveFormattingIcon,
  TypeIcon,
  UnderlineIcon,
} from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, RICH_TEXT_SIZES, type RichTextSize } from '@/shared/lib';
import { IconButton } from '../IconButton';
import { RichTextFormats, RichTextInlineFormat, RichTextListType } from './types';

type Props = {
  formats: RichTextFormats;
  disabled?: boolean;
  onToggleFormat: (name: RichTextInlineFormat) => void;
  /** Выбор размера выделенной части; не задан — кнопок размера на панели нет */
  onToggleSize?: (size: RichTextSize) => void;
  onToggleList: (type: RichTextListType) => void;
  onClearFormat: () => void;
};

/** Подписи кнопок размера части текста. */
const SIZE_LABEL_KEY: Record<RichTextSize, `richTextEditor.sizes.${RichTextSize}`> = {
  small: 'richTextEditor.sizes.small',
  medium: 'richTextEditor.sizes.medium',
  large: 'richTextEditor.sizes.large',
};

/** Знак кнопки размера тем крупнее, чем крупнее размер. */
const SIZE_ICON_CLASS: Record<RichTextSize, string> = {
  small: 'size-3',
  medium: 'size-4',
  large: 'size-5',
};

/** Кнопка тулбара: подсвечивается, когда формат активен в текущем выделении */
const ToolbarButton: FC<{
  isActive?: boolean;
  label: string;
  disabled?: boolean;
  icon: React.ReactNode;
  onClick: () => void;
}> = ({ isActive, label, disabled, icon, onClick }) => (
  <IconButton
    variant="ghost"
    size="icon"
    type="button"
    label={label}
    // Приводим к булеву типу: Quill отдаёт неактивный формат как undefined, и атрибут пропал бы из разметки
    aria-pressed={Boolean(isActive)}
    disabled={disabled}
    icon={icon}
    className={cn('size-7', isActive && 'bg-accent text-accent-foreground')}
    // Не отдаём фокус кнопке — иначе Quill теряет выделение и форматировать становится нечего
    onMouseDown={(event) => event.preventDefault()}
    onClick={onClick}
  />
);

/**
 * Панель форматирования {@link RichTextEditor}.
 *
 * Набор кнопок ограничен форматами, которые переживают санитизацию при просмотре
 * (см. `RICH_TEXT_ALLOWED_TAGS`): ссылки сюда сознательно не входят. Размер выделенной части —
 * только у редактора, где он выбирается.
 */
export const RichTextEditorToolbar: FC<Props> = ({
  formats,
  disabled,
  onToggleFormat,
  onToggleSize,
  onToggleList,
  onClearFormat,
}) => {
  const { t } = useTranslation('common');

  return (
    <div className="flex items-center gap-0.5">
      <ToolbarButton
        isActive={formats.bold}
        label={t('richTextEditor.bold')}
        disabled={disabled}
        icon={<BoldIcon className="size-4" />}
        onClick={() => onToggleFormat('bold')}
      />
      <ToolbarButton
        isActive={formats.italic}
        label={t('richTextEditor.italic')}
        disabled={disabled}
        icon={<ItalicIcon className="size-4" />}
        onClick={() => onToggleFormat('italic')}
      />
      <ToolbarButton
        isActive={formats.underline}
        label={t('richTextEditor.underline')}
        disabled={disabled}
        icon={<UnderlineIcon className="size-4" />}
        onClick={() => onToggleFormat('underline')}
      />

      {onToggleSize && (
        <>
          <Separator orientation="vertical" className="mx-1 h-5" />
          {RICH_TEXT_SIZES.map((size) => (
            <ToolbarButton
              key={size}
              isActive={formats.size === size}
              label={t(SIZE_LABEL_KEY[size])}
              disabled={disabled}
              icon={<TypeIcon className={SIZE_ICON_CLASS[size]} />}
              onClick={() => onToggleSize(size)}
            />
          ))}
        </>
      )}

      <Separator orientation="vertical" className="mx-1 h-5" />

      <ToolbarButton
        isActive={formats.list === 'bullet'}
        label={t('richTextEditor.bulletList')}
        disabled={disabled}
        icon={<ListIcon className="size-4" />}
        onClick={() => onToggleList('bullet')}
      />
      <ToolbarButton
        isActive={formats.list === 'ordered'}
        label={t('richTextEditor.orderedList')}
        disabled={disabled}
        icon={<ListOrderedIcon className="size-4" />}
        onClick={() => onToggleList('ordered')}
      />

      <Separator orientation="vertical" className="mx-1 h-5" />

      <ToolbarButton
        label={t('richTextEditor.clearFormat')}
        disabled={disabled}
        icon={<RemoveFormattingIcon className="size-4" />}
        onClick={onClearFormat}
      />
    </div>
  );
};
